import React from 'react';
import PropTypes from 'prop-types';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPaperPlane, faPlus } from '@fortawesome/free-solid-svg-icons';
import EmojiPickerPopover from './EmojiPickerPopover';
import ComposerAttachmentMenu from './ComposerAttachmentMenu';

const ComposerFooter = ({
  isDark,
  onApplyFormat,
  showEmojiPicker,
  setShowEmojiPicker,
  emojiPickerRef,
  onInsertEmoji,
  showPlusMenu,
  setShowPlusMenu,
  plusMenuRef,
  onSelectPlusAction,
  isSending,
  canSend,
  onSubmit
}) => {
  return (
    <div className="d-flex align-items-center justify-content-between px-3 py-2">
      {/* Menú de adjuntos (+) y herramientas de formato a la izquierda */}
      <div className="d-flex align-items-center gap-1.5 position-relative">
        {/* Botón '+' de WhatsApp para abrir menú de adjuntos y acciones */}
        <button
          type="button"
          className="btn btn-sm btn-plus-attach rounded-circle d-flex align-items-center justify-content-center border-0 shadow-none p-0 flex-shrink-0"
          style={{
            width: '30px',
            height: '30px',
            backgroundColor: showPlusMenu
              ? (isDark ? '#334155' : '#e2e8f0')
              : (isDark ? '#1e293b' : '#f1f5f9'),
            color: isDark ? '#cbd5e1' : '#334155',
            transition: 'all 0.18s ease'
          }}
          title="Adjuntar o agregar nota"
          onClick={() => setShowPlusMenu(!showPlusMenu)}
          disabled={isSending}
        >
          <FontAwesomeIcon
            icon={faPlus}
            style={{
              fontSize: '0.85rem',
              transform: showPlusMenu ? 'rotate(45deg)' : 'none',
              transition: 'transform 0.18s ease'
            }}
          />
        </button>

        {/* Menú flotante estilo WhatsApp Web */}
        <ComposerAttachmentMenu
          isOpen={showPlusMenu}
          onClose={() => setShowPlusMenu(false)}
          onSelectAction={onSelectPlusAction}
          menuRef={plusMenuRef}
          isDark={isDark}
        />

        {/* Botones de formato WhatsApp */}
        <div className="d-flex align-items-center gap-1 ms-1">
          <button
            type="button"
            className="btn btn-sm p-0 px-1.5 border-0 shadow-none"
            style={{
              fontWeight: 700,
              fontSize: '0.84rem',
              color: isDark ? '#cbd5e1' : '#475569',
              minWidth: '22px'
            }}
            title="Negrita (*texto*) - Ctrl+B"
            onClick={() => onApplyFormat('*', '*', 'negrita')}
          >
            B
          </button>
          <button
            type="button"
            className="btn btn-sm p-0 px-1.5 border-0 shadow-none"
            style={{
              fontFamily: 'serif',
              fontStyle: 'italic',
              fontWeight: 600,
              fontSize: '0.84rem',
              color: isDark ? '#cbd5e1' : '#475569',
              minWidth: '22px'
            }}
            title="Cursiva (_texto_) - Ctrl+I"
            onClick={() => onApplyFormat('_', '_', 'cursiva')}
          >
            I
          </button>
          <button
            type="button"
            className="btn btn-sm p-0 px-1.5 border-0 shadow-none"
            style={{
              textDecoration: 'line-through',
              fontWeight: 600,
              fontSize: '0.84rem',
              color: isDark ? '#cbd5e1' : '#475569',
              minWidth: '22px'
            }}
            title="Tachado (~texto~) - Ctrl+Shift+X"
            onClick={() => onApplyFormat('~', '~', 'tachado')}
          >
            S
          </button>
          <button
            type="button"
            className="btn btn-sm p-0 px-1.5 border-0 shadow-none font-monospace"
            style={{
              fontWeight: 600,
              fontSize: '0.78rem',
              color: isDark ? '#cbd5e1' : '#475569',
              minWidth: '24px'
            }}
            title="Monoespaciado (```texto```)"
            onClick={() => onApplyFormat('```', '```', 'código')}
          >
            &lt;/&gt;
          </button>

          {/* Separador vertical */}
          <div
            className="mx-1"
            style={{
              width: '1px',
              height: '14px',
              backgroundColor: isDark ? '#334155' : '#cbd5e1'
            }}
          />

          {/* Botón Emojis */}
          <button
            type="button"
            className="btn btn-sm p-0 px-1.5 border-0 shadow-none"
            style={{
              fontSize: '0.92rem',
              lineHeight: 1,
              color: isDark ? '#cbd5e1' : '#475569'
            }}
            title="Insertar emoji"
            onClick={() => setShowEmojiPicker(!showEmojiPicker)}
          >
            😊
          </button>

          {/* Emoji Picker Popover */}
          {showEmojiPicker && (
            <div
              ref={emojiPickerRef}
              className="position-absolute shadow-lg"
              style={{
                bottom: '100%',
                left: '36px',
                marginBottom: '8px',
                zIndex: 1060
              }}
            >
              <EmojiPickerPopover
                onSelectEmoji={onInsertEmoji}
                onClose={() => setShowEmojiPicker(false)}
                isDark={isDark}
              />
            </div>
          )}
        </div>
      </div>

      {/* Botón Circular Enviar a la derecha */}
      <div className="d-flex align-items-center">
        <button
          type="button"
          className="rounded-circle border-0 d-flex align-items-center justify-content-center text-white shadow-sm flex-shrink-0"
          style={{
            width: '36px',
            height: '36px',
            backgroundColor: '#059669',
            cursor: !canSend ? 'not-allowed' : 'pointer',
            opacity: !canSend ? 0.6 : 1,
            transition: 'all 0.15s ease'
          }}
          title="Enviar mensaje (Enter) • Shift + Enter para salto de línea"
          disabled={!canSend}
          onClick={onSubmit}
        >
          {isSending ? (
            <div className="spinner-border spinner-border-sm text-white" role="status" />
          ) : (
            <FontAwesomeIcon icon={faPaperPlane} style={{ fontSize: '0.88rem' }} />
          )}
        </button>
      </div>
    </div>
  );
};

ComposerFooter.propTypes = {
  isDark: PropTypes.bool,
  onApplyFormat: PropTypes.func.isRequired,
  showEmojiPicker: PropTypes.bool.isRequired,
  setShowEmojiPicker: PropTypes.func.isRequired,
  emojiPickerRef: PropTypes.object.isRequired,
  onInsertEmoji: PropTypes.func.isRequired,
  showPlusMenu: PropTypes.bool.isRequired,
  setShowPlusMenu: PropTypes.func.isRequired,
  plusMenuRef: PropTypes.object.isRequired,
  onSelectPlusAction: PropTypes.func.isRequired,
  isSending: PropTypes.bool.isRequired,
  canSend: PropTypes.bool.isRequired,
  onSubmit: PropTypes.func.isRequired
};

export default ComposerFooter;
